import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {CheckIcon} from '@/components/ui/icon/Icon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {Label} from '@/components/ui/label/Label'
import {RadioBoxItem} from '@/components/ui/radio-box-atoms/RadioBoxAtoms'
import {Text} from '@/components/ui/text/Text'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {ProjectExtended, ProjectType} from '@/features/projects/types'
import {projectTypeToLabel} from '@/features/projects/utils'
import {useTranslation} from '@/translations/i18n'
import {ObjectKeys} from '@/utilities/helpers'
import {Root} from '@radix-ui/react-toggle-group'
import {useController, useFormContext} from 'react-hook-form'
import {useTheme} from 'styled-components'
import {z} from 'zod'

const ProjectTypeFieldsSchema = ProjectFieldsSchema.pick({
    projectType: true,
    numberOfCycles: true
})
type ProjectTypeFieldsSchemaType = z.infer<typeof ProjectTypeFieldsSchema>

export const ProjectTypeFields = ({disabled, project}: {disabled?: ProjectType[]; project?: ProjectExtended}) => {
    const {t} = useTranslation()
    const {control, resetField} = useFormContext<ProjectTypeFieldsSchemaType>()
    const {field, fieldState} = useController({control, name: 'projectType'})
    const {palette} = useTheme()

    const handleProjectTypeChange = (value: string) => {
        field.onChange(value || undefined)
        resetField('numberOfCycles')
    }

    return (
        <Flexbox direction="column" gap={1.5} fullWidth>
            <Label>{t('projects:project_type')}</Label>
            <Root
                type="single"
                value={field.value || ''}
                onValueChange={handleProjectTypeChange}
                style={{width: '100%'}}
                disabled={project?.isOpeningEventCompleted}
            >
                <Grid columns={2} gap={4} fullWidth>
                    {ObjectKeys(projectTypeToLabel).map(key => (
                        <GridItem fullWidth key={key}>
                            <RadioBoxItem
                                disabled={disabled?.includes(key)}
                                name={field.name}
                                value={key}
                                error={!!fieldState.error?.message}
                            >
                                {t(projectTypeToLabel[key])}
                            </RadioBoxItem>
                        </GridItem>
                    ))}
                </Grid>
            </Root>
            {fieldState.error?.message && <InputHelpText error={fieldState.error?.message} />}
            {project && project.isOpeningEventCompleted && (
                <Text
                    size="textXs"
                    palette="neutral"
                    shade="500"
                    weight="500"
                    style={{display: 'flex', alignItems: 'center', gap: 4}}
                >
                    <CheckIcon size={14} color={palette.success[500]} />
                    {project?.type == 'cyclic'
                        ? t('projects:completed_opening_event', {
                              type: t('agenda:access_cycle_opening_processing').toLowerCase()
                          })
                        : t('projects:completed_opening_event', {
                              type: t('agenda:access_project_opening_processing').toLowerCase()
                          })}
                </Text>
            )}
        </Flexbox>
    )
}
